import { useState } from "react";
import { Plus, QrCode } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { useCompany } from "../../components/layout/CompanyLayout";
import { Loader, ErrorState, EmptyState, PageHeader } from "../../components/common/States";
import CrudDialog from "../../components/common/CrudDialog";
import QrCard from "../../components/company/QrCard";

const DEFAULT_MESSAGE = "Votre avis nous aide à améliorer nos services. Scannez ce code et partagez votre expérience.";

export default function QrCodes() {
  const { can, company } = useCompany();
  const { data, loading, error, reload } = useApi("/company/qrcodes");
  const agencies = useApi("/company/agencies");
  const services = useApi("/company/services");
  const [creating, setCreating] = useState(false);
  const [agencyFilter, setAgencyFilter] = useState("");
  const manage = can("qr:update");
  const name = (list, id) => list.data?.find((x) => x.id === id)?.name;
  const visible = (data || []).filter((q) => !agencyFilter || q.agency_id === agencyFilter);
  const fields = [
    { name: "label", label: "Libellé (ex : Guichet 2, Hall d'accueil)", required: true },
    { name: "agency_id", label: "Agence", type: "select", nullable: true, half: true, options: [["", "Non précisée"], ...(agencies.data || []).map((a) => [a.id, a.name])] },
    { name: "service_id", label: "Service", type: "select", nullable: true, half: true, options: [["", "Non précisé"], ...(services.data || []).map((s) => [s.id, s.name])] },
    { name: "message", label: "Message de l'affiche", required: true },
  ];

  return (
    <div>
      <PageHeader title="QR codes" description="Un QR code unique est généré automatiquement pour chaque agence et chaque service. Il ouvre directement le formulaire associé."
        actions={<>
          <select className="field-select w-48" value={agencyFilter} onChange={(e) => setAgencyFilter(e.target.value)} data-testid="qr-agency-filter">
            <option value="">Toutes les agences</option>{(agencies.data || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          {can("qr:create") && <Button onClick={() => setCreating(true)} className="rounded-full" data-testid="qr-create-button"><Plus className="mr-1.5 h-4 w-4" />QR code supplémentaire</Button>}
        </>} />
      {company.status !== "active" && <p className="mb-4 rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-900">Les QR codes deviendront utilisables par le public après validation de votre entreprise.</p>}
      {loading && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !data.length && <EmptyState icon={QrCode} title="Aucun QR code" text="Générez un QR code pour un guichet, une agence ou un service." />}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visible.map((qr) => (
          <QrCard key={qr.id} qr={qr} manage={manage} company={company} onChange={reload}
            labels={[name(agencies, qr.agency_id), name(services, qr.service_id)].filter(Boolean).join(" · ") || "Toute l'organisation"} />
        ))}
      </div>
      {creating && (
        <CrudDialog open onOpenChange={setCreating} testId="qr" fields={fields} title="Nouveau QR code"
          initial={{ label: "", agency_id: "", service_id: "", message: DEFAULT_MESSAGE }} endpoint="/company/qrcodes" method="post" onSaved={() => reload()} />
      )}
    </div>
  );
}
